import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Typography, Grid, TextField, Paper, useMediaQuery } from '@material-ui/core/';
import { useTheme } from '@material-ui/core/styles';
import { selectUser } from '~/store/legacy/selector/userSelectors';
import { selectShoppingCartData } from '~/store/legacy/selector/shoppingCartSelectors';
import { updateUserAddress } from '~/store/legacy/actions/userActions';
import { setDeliveryNote as setDeliveryNoteAction } from '~/store/legacy/actions/shoppingCartActions';
import InternationalPhoneDisplay from '~/components/InternationalPhoneDisplay';
import Subtitle from '../ui-kit/Subtitle/Subtitle';
import PhoneNumber from './PhoneNumber';
import InputSectionLayout from './InputSectionLayout';
import useStyles from './AddressSection.style';

const AddressSectionTypography = ({ children, ...rest }) => {
  const classes = useStyles();
  return (
    <Typography variant='body1' className={classes.address} {...rest}>
      {children}
    </Typography>
  );
};

const AddressSectionTextField = (props) => {
  const classes = useStyles();
  return (
    <TextField
      fullWidth
      InputProps={{
        classes: {
          notchedOutline: classes.textField,
        },
      }}
      variant='outlined'
      {...props}
    />
  );
};

/**
 * Display Address and Phone number
 */
const AddressSection = (props) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { isDelivery } = props;
  const { addresses } = useSelector(selectUser);
  const { addressId, meals } = useSelector(selectShoppingCartData);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const getAddressString = (addressPartsArray) => {
    return `${addressPartsArray.filter((part) => !!part).join(', ')}`;
  };

  const getAddress = () => {
    if (isDelivery) {
      return addresses.find((address) => String(address.id) === String(addressId));
    } else {
      const meal = meals[0];
      const {
        restaurant: { data: restaurant },
      } = meal.relationships;
      return restaurant.relationships.address.data.attributes;
    }
  };

  const renderAddressField = (address) => {
    if (!address || (isDelivery && !address.address_1)) {
      return (
        <Typography color='primary' variant='subtitle1'>
          <FormattedMessage id='page.checkout.no_address' />
        </Typography>
      );
    }
    const { address_1, address1, address_2, address2, city, state, zip_code, zipCode } = address;
    const addressString = `${getAddressString([
      address_1,
      address1,
      address_2,
      address2,
      city,
      state,
    ])} ${zip_code || zipCode}`;
    return <AddressSectionTypography>{addressString}</AddressSectionTypography>;
  };

  const setAddress2 = (e) => {
    if (!e.target.value) {
      return;
    }
    const newAddress = {
      ...getAddress(),
      ...{
        address_2: e.target.value,
      },
    };
    dispatch(updateUserAddress({ address: newAddress }));
  };

  const setDeliveryNote = (e) => {
    dispatch(setDeliveryNoteAction(e.target.value));
  };

  const renderDeliveryFields = () => {
    const address = getAddress();
    return (
      <>
        {renderAddressField(address)}
        {address && !address.address_2 && (
          <AddressSectionTextField name='address2' placeholder='Unit #' onBlur={setAddress2} />
        )}
        <AddressSectionTextField
          name='deliveryInfo'
          placeholder='Add Delivery Instructions'
          onBlur={setDeliveryNote}
        />
      </>
    );
  };

  const renderPickupFields = () => {
    const meal = meals[0];
    const {
      restaurant: {
        data: {
          attributes: { phoneNumber },
        },
      },
    } = meal.relationships;
    const address = getAddress();
    return (
      <>
        <AddressSectionTypography>
          {' '}
          Restaurant's number: <InternationalPhoneDisplay phoneNumber={phoneNumber} />{' '}
        </AddressSectionTypography>
        {renderAddressField(address)}
      </>
    );
  };

  const getMapLink = () => {
    const { address1, address2, city, state, zipCode } = getAddress();
    const addressString = getAddressString([address1, address2, city, state, zipCode]);
    return encodeURI(`https://www.google.com/maps/search/?api=1&query=${addressString}`);
  };

  return (
    <Grid container className={classes.field}>
      <InputSectionLayout
        left={
          <>
            <Grid
              container
              alignItems='center'
              justify='space-between'
              className={!isDelivery ? classes.pickupHeader : ''}>
              <Grid item xs={12} sm={10}>
                <Subtitle hLevel={6}>
                  {isDelivery ? 'Contact & Delivery Address' : 'Restaurant Contact & Address'}
                </Subtitle>
              </Grid>
              {!isDelivery && (
                <Grid item container={isMobile} justify='flex-end'>
                  <a href={getMapLink()} target='_blank' rel='noreferrer' className={classes.link}>
                    Map View
                  </a>
                </Grid>
              )}
            </Grid>

            {isDelivery && <PhoneNumber />}
            <Paper variant='outlined' className={classes.deliveryAddressContainer}>
              {isDelivery && renderDeliveryFields()}
              {!isDelivery && renderPickupFields()}
            </Paper>
          </>
        }></InputSectionLayout>
    </Grid>
  );
};

export default AddressSection;
